import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RubricCore } from 'Types/dealGPT';

import { getRubricCore } from '../services/dealGPT';

/**
 * Custom hook to fetch and manage rubric core data based on the provided scope.
 *
 * @param {string | undefined} scope - The scope for which to fetch the rubric core data.
 * @returns {object} An object containing:
 * - `rubricCoreByArea`: A record grouped by area and dimension containing arrays of `RubricCore` items.
 * - `rubricType`: The type of the rubric, derived from the first item in the grouped data.
 *
 * @example
 * const { rubricCoreByArea, rubricType } = useRubricCore(scope);
 */
export const useRubricCore = (scope: string | undefined) => {
  const [rubricCoreByArea, setRubricCoreByArea] = useState<
    Record<string, Record<string, RubricCore[]>>
  >({});

  // Ref to store the previous scope value, used to prevent unnecessary fetches
  const prevScopeRef = useRef<string | undefined>();

  useEffect(() => {
    // Little optimization to prevent unnecessary fetches when parent component re-renders
    if (!scope || scope === prevScopeRef.current) return;
    prevScopeRef.current = scope;

    const getCore = async () => {
      try {
        const core = await getRubricCore(scope);
        if (core.length === 0) return;

        // Group by area and dimension
        const grouped = core.reduce(
          (acc: Record<string, Record<string, RubricCore[]>>, item: RubricCore) => {
            acc[item.area] = acc[item.area] || {};
            acc[item.area][item.dimension] = acc[item.area][item.dimension] || [];
            acc[item.area][item.dimension].push(item);
            return acc;
          },
          {}
        );

        setRubricCoreByArea(grouped);
      } catch (error) {
        enqueueSnackbar('Failed to fetch rubric core data. Please try again later.', {
          variant: 'error',
        });
        return;
      }
    };

    getCore();
  }, [scope, prevScopeRef]);

  const rubricType = useMemo(() => {
    const list = Object.values(rubricCoreByArea).flatMap((area) => Object.values(area));

    let rubricType = list.flat()[0]?.rubricType;

    if ((!rubricType || rubricType === 'Numeric') && list.length > 0) {
      rubricType = String(list[0].length);
    }

    return rubricType;
  }, [rubricCoreByArea]);

  return { rubricCoreByArea, rubricType };
};
